import { Controller } from "@hotwired/stimulus";
import { put } from "@rails/request.js";
import { computeItemPrice, computeAdjustmentPrice } from "../configurator/pricing";
import { collectWarnings } from "../configurator/validation";
import { interpolate } from "../lib/interpolate";

// Wording arrives from the view as %{...} templates, so warnings follow the
// domain's language the same way the rest of the page does.
function warningMessage(messages, key, variant) {
  if (key === "out_of_stock") {
    const stock = Number(variant?.stock ?? 0);
    return stock <= 0
      ? interpolate(messages.out_of_stock_none)
      : interpolate(messages.out_of_stock_quantity, { stock });
  }

  return interpolate(messages[key], variant ?? {});
}

// Matches Variant#in_stock? in app/models/variant.rb:
// untracked → always true; stock ≤ 0 → false; otherwise stock ≥ quantity.
function variantHasStock(variant, quantity) {
  if (!variant?.stock_tracked) return true;
  const stock = Number(variant.stock ?? 0);
  if (stock <= 0) return false;
  return stock >= Number(quantity);
}

export default class extends Controller {
  static outlets = ["adjustment-selector", "product-preview"];

  static values = {
    // Warning templates, keyed the same way collectWarnings names them.
    warningMessages: { type: Object, default: {} },
    variants: { type: Array, default: [] },
    currentVariantId: { type: Number, default: 0 },
    currentAdjustmentConfigurationId: { type: Number, default: 0 },
    roundedCorner: { type: Boolean, default: false },
    adjustments: { type: Array, default: [] },
    domain: { type: Number, default: 0 },
    role: { type: String, default: "basic" },
    draftUrl: { type: String, default: "" },
    // Endpoint rendering the product image partial for the current variant;
    // empty on views without a product image (mobile sheet, empty state).
    productImageUrl: { type: String, default: "" },
    // Option A (see cardSublineTarget update below): map of option_value_id => label string,
    // so the mobile collapsed-card subline can be rebuilt client-side after a variant change.
    optionValueLabels: { type: Object, default: {} },
    // Collapsed-card subline before any option is picked (mobile only).
    chooseOptionsLabel: { type: String, default: "" },
    // Label shown on the submit/progress button while the order is not yet
    // submittable. Both views provide it; desktop says "add to cart", mobile
    // says "finish the configuration".
    incompleteLabel: { type: String, default: "" },
    // Template for the submittable state, e.g. "Objednať za %{price}", plus the
    // domain's currency symbol -- this button used to hard-code the euro sign,
    // which would have priced the Czech site in the wrong currency.
    submitLabel: { type: String, default: "" },
    currencySymbol: { type: String, default: "" },
    // Slovak CLDR plural forms for the photo noun, provided via i18n on mobile.
    // Desktop omits these — `renderStep3State` short-circuits on the missing
    // `step3Badge` target, so the empty defaults are never read.
    photoNounOne: { type: String, default: "" },
    photoNounFew: { type: String, default: "" },
    photoNounOther: { type: String, default: "" },
    // Standalone subline copy for the no-photos state.
    uploadPrompt: { type: String, default: "" },
    // CTA hint appended to the subline when photos already exist.
    uploadMore: { type: String, default: "" }
  };

  static targets = [
    "price",
    "summary",
    "warnings",
    "warningsBox",
    "submit",
    "formData",
    "variantIdField",
    "productImageFrame",
    "photoRow",
    "whiteFrameRow",
    "whiteFrameCheckbox",
    "cardSubline",
    "step2Badge",
    "step3Badge",
    "step3Subline"
  ];

  connect() {
    // Sync the hidden variant_id field from the server-rendered initial value
    // so form submission works when the page is loaded with ?variant_id=X in
    // the URL (e.g. after a hard refresh).
    if (this.hasVariantIdFieldTarget && this.currentVariantIdValue) {
      this.variantIdFieldTarget.value = String(this.currentVariantIdValue);
    }
    this.draftPersistTimer = null;
    this.refresh();
  }

  disconnect() {
    if (this.draftPersistTimer) clearTimeout(this.draftPersistTimer);
  }

  variantIdChanged(value) {
    this.currentVariantIdValue = Number(value) || 0;
    if (this.hasVariantIdFieldTarget) {
      this.variantIdFieldTarget.value = String(this.currentVariantIdValue);
    }
    this.refresh();
    this.refreshProductImage();
    if (this.hasAdjustmentSelectorOutlet) {
      this.adjustmentSelectorOutlet.variantChanged();
    }
    this.scheduleDraftPersist();
  }

  // The product image is server-rendered (variant image with product
  // fallback), so reload its turbo frame to track the selected variant.
  refreshProductImage() {
    if (!this.hasProductImageFrameTarget || !this.productImageUrlValue) return;
    const url = new URL(this.productImageUrlValue, window.location.origin);
    url.searchParams.set("variant_id", this.currentVariantIdValue || "");
    this.productImageFrameTarget.src = url.toString();
  }

  adjustmentIdChanged(value) {
    this.currentAdjustmentConfigurationIdValue = Number(value) || 0;
    this.refresh();
    this.scheduleDraftPersist();
  }

  roundedCornerToggled(value) {
    this.roundedCornerValue = Boolean(value);
    this.refresh();
    this.scheduleDraftPersist();
  }

  photoSelectionChanged() {
    this.refresh();
    this.scheduleDraftPersist();
  }

  photoQuantityChanged() {
    this.refresh();
    this.scheduleDraftPersist();
  }

  photoRowTargetConnected() {
    // Re-render when new photo rows show up (upload, filter switch).
    this.refresh();
  }

  photoRowTargetDisconnected() {
    this.refresh();
  }

  refresh() {
    const state = this.readState();
    const itemPrice = computeItemPrice({
      variant: state.variant,
      photosQuantity: state.photosQuantity,
      roundedCorner: state.roundedCorner,
      domain: state.domain,
      role: state.role
    });
    const adjustmentPrice = computeAdjustmentPrice({
      adjustment: state.adjustment,
      variant: state.variant,
      photosQuantity: state.photosQuantity
    });
    const total = itemPrice + adjustmentPrice;

    const warningKeys = this.buildWarningKeys(state);
    this.renderPrice(total);
    this.renderWarnings(warningKeys, state.variant);
    this.toggleSubmit(warningKeys.length === 0);
    this.renderWhiteFrameRow(state.variant);
    this.renderCardSubline(state.variant);
    this.renderStep3State();
  }

  // Mirrors the server-rendered subline in show.html+mobile.erb:
  //   @variant&.variant_configurations&.map { |vc| vc.option_value.value }&.join(" · ")
  // Option A: pull human labels from optionValueLabelsValue, keyed by option_value_id.
  renderCardSubline(variant) {
    if (!this.hasCardSublineTarget) return;
    const labels = this.optionValueLabelsValue || {};
    const parts = (variant?.options || [])
      .map((o) => labels[String(o.option_value_id)])
      .filter(Boolean);
    const configured = parts.length > 0;
    this.cardSublineTarget.textContent = configured
      ? parts.join(" · ")
      : this.chooseOptionsLabelValue;
    this.renderStep2State(configured);
  }

  // Mobile only (guarded): toggle the Step 2 badge color when a variant is
  // selected. The badge holds a server-rendered SVG icon — we don't touch its
  // content, only the background color (brand bordeaux for done, slate for pending).
  renderStep2State(configured) {
    if (!this.hasStep2BadgeTarget) return;
    this.step2BadgeTarget.classList.toggle("bg-rose-700", configured);
    this.step2BadgeTarget.classList.toggle("bg-slate-900", !configured);
  }

  // Slovak CLDR cardinal plural rules: one (n=1), few (n∈{2,3,4}), other (everything else).
  pluralizePhotos(count) {
    if (count === 1) return this.photoNounOneValue;
    if (count >= 2 && count <= 4) return this.photoNounFewValue;
    return this.photoNounOtherValue;
  }

  // Mobile only (guarded): toggle the Step 3 badge color + update the subline
  // based on how many photos are uploaded. The badge holds a server-rendered
  // SVG icon (image) — only the background color is touched here.
  renderStep3State() {
    if (!this.hasStep3BadgeTarget) return;
    const count = this.photoRowTargets.length;
    const hasPhotos = count > 0;
    this.step3BadgeTarget.classList.toggle("bg-rose-700", hasPhotos);
    this.step3BadgeTarget.classList.toggle("bg-slate-900", !hasPhotos);
    if (this.hasStep3SublineTarget) {
      this.step3SublineTarget.textContent = hasPhotos
        ? `${count} ${this.pluralizePhotos(count)} · ${this.uploadMoreValue}`
        : this.uploadPromptValue;
    }
  }

  renderWhiteFrameRow(variant) {
    if (!this.hasWhiteFrameRowTarget) return;
    const enabled = !!variant?.enable_white_frame_option;
    this.whiteFrameRowTarget.classList.toggle("hidden", !enabled);
    if (!enabled && this.hasWhiteFrameCheckboxTarget) {
      this.whiteFrameCheckboxTarget.checked = false;
      if (this.hasProductPreviewOutlet) this.productPreviewOutlet.resetFrame();
    }
  }

  buildWarningKeys(state) {
    const keys = [];
    if (!state.variant) keys.push("no_variant");
    if (state.selectedRows.length === 0) keys.push("no_selection");
    if (state.selectedRows.some((r) => r.quantity < 1)) keys.push("zero_quantity");
    if (state.variant && state.selectedRows.length > 0) {
      const ppu = state.variant.photos_per_unit || 1;
      const dividedQuantity = Math.floor(state.photosQuantity / ppu);
      collectWarnings({
        variant: state.variant,
        quantity: dividedQuantity,
        photosQuantity: state.photosQuantity
      }).forEach((k) => keys.push(k));
      if (state.variant.stock_tracked && !variantHasStock(state.variant, dividedQuantity)) {
        keys.push("out_of_stock");
      }
    }
    return keys;
  }

  readState() {
    const variant = this.variantsValue.find((v) => v.id === this.currentVariantIdValue) || null;
    const adjustment = this.adjustmentsValue.find((a) => a.id === this.currentAdjustmentConfigurationIdValue) || null;
    const selectedRows = this.photoRowTargets
      .filter((row) => row.dataset.selected === "true")
      .map((row) => ({
        photoId: Number(row.dataset.photoId),
        quantity: Number(row.dataset.quantity) || 0
      }));
    const photosQuantity = selectedRows.reduce((sum, r) => sum + r.quantity, 0);
    return {
      variant,
      adjustment,
      roundedCorner: this.roundedCornerValue,
      domain: this.domainValue,
      role: this.roleValue,
      selectedRows,
      photosQuantity
    };
  }

  renderPrice(total) {
    if (!this.hasPriceTarget) return;
    this.priceTarget.textContent = total > 0
      ? interpolate(this.submitLabelValue, {
          price: `${(total / 1000).toFixed(2)} ${this.currencySymbolValue}`
        })
      : this.incompleteLabelValue;
  }

  renderWarnings(keys, variant) {
    if (!this.hasWarningsTarget) return;
    const v = variant || {};
    this.warningsTarget.innerHTML = keys
      .map((k) => {
        const msg = warningMessage(this.warningMessagesValue, k, v);
        return msg
          ? `<span class="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-900 ring-1 ring-inset ring-amber-200">${msg}</span>`
          : "";
      })
      .filter(Boolean)
      .join("");
    if (this.hasWarningsBoxTarget) {
      this.warningsBoxTarget.classList.toggle("hidden", keys.length === 0);
    }
  }

  toggleSubmit(enabled) {
    if (!this.hasSubmitTarget) return;
    this.submitTarget.disabled = !enabled;
  }

  scheduleDraftPersist() {
    if (!this.draftUrlValue) return;
    if (this.draftPersistTimer) clearTimeout(this.draftPersistTimer);
    this.draftPersistTimer = setTimeout(() => this.persistDraft(), 500);
  }

  async persistDraft() {
    this.draftPersistTimer = null;
    if (!this.draftUrlValue) return;
    await put(this.draftUrlValue, {
      body: JSON.stringify({ configurator_draft: this.serializeDraft() }),
      contentType: "application/json",
      responseKind: "json"
    });
  }

  serializeDraft() {
    const state = this.readState();
    const photos = {};
    state.selectedRows.forEach((r) => {
      if (r.quantity > 0) photos[String(r.photoId)] = r.quantity;
    });
    return {
      product_id: state.variant?.product_id ?? null,
      variant_id: state.variant?.id ?? null,
      adjustment_configuration_id: state.adjustment?.id ?? null,
      rounded_corner: !!state.roundedCorner,
      photos
    };
  }

  serializeForSubmit() {
    const state = this.readState();
    const payload = state.selectedRows
      .filter((r) => r.quantity > 0)
      .map((r) => ({ photo_id: r.photoId, quantity: r.quantity }));
    if (this.hasFormDataTarget) {
      this.formDataTarget.value = JSON.stringify(payload);
    }
  }
}
